import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box } from '@mui/material';
import Dashboard from './components/Dashboard';
import DailySurvey from './components/DailySurvey';
import Statistics from './components/Statistics';
import Settings from './components/Settings';
import { SurveyData, HealthMetrics } from './types';

const App: React.FC = () => {
  const [surveyData, setSurveyData] = useState<SurveyData[]>([]);
  const [healthMetrics, setHealthMetrics] = useState<HealthMetrics>({
    overallScore: 0,
    stressLevel: 0,
    energyLevel: 0,
    sleepQuality: 0,
    physicalActivity: 0,
    nutritionScore: 0,
    hydrationLevel: 0,
    moodScore: 0,
    focusLevel: 0,
    lastUpdated: new Date(),
  });

  useEffect(() => {
    // Load data from localStorage on app start
    const savedSurveyData = localStorage.getItem('peakstate-survey-data');
    const savedHealthMetrics = localStorage.getItem('peakstate-health-metrics');
    
    if (savedSurveyData) {
      setSurveyData(JSON.parse(savedSurveyData));
    }
    
    if (savedHealthMetrics) {
      setHealthMetrics(JSON.parse(savedHealthMetrics));
    }
  }, []);

  const handleSurveySubmit = (newSurveyData: SurveyData) => {
    const updatedData = [...surveyData, newSurveyData];
    setSurveyData(updatedData);
    localStorage.setItem('peakstate-survey-data', JSON.stringify(updatedData));
    
    // Calculate new health metrics
    const newMetrics = calculateHealthMetrics(updatedData);
    setHealthMetrics(newMetrics);
    localStorage.setItem('peakstate-health-metrics', JSON.stringify(newMetrics));
  };

  const calculateHealthMetrics = (data: SurveyData[]): HealthMetrics => {
    if (data.length === 0) {
      return {
        overallScore: 0,
        stressLevel: 0,
        energyLevel: 0,
        sleepQuality: 0,
        physicalActivity: 0,
        nutritionScore: 0,
        hydrationLevel: 0,
        moodScore: 0,
        focusLevel: 0,
        lastUpdated: new Date(),
      };
    }

    const latest = data[data.length - 1];
    const avgStress = data.reduce((sum, d) => sum + d.stressLevel, 0) / data.length;
    const avgEnergy = data.reduce((sum, d) => sum + d.energyLevel, 0) / data.length;
    const avgSleep = data.reduce((sum, d) => sum + d.sleepQuality, 0) / data.length;
    const avgActivity = data.reduce((sum, d) => sum + d.physicalActivity, 0) / data.length;
    const avgNutrition = data.reduce((sum, d) => sum + d.nutritionScore, 0) / data.length;
    const avgHydration = data.reduce((sum, d) => sum + d.hydrationLevel, 0) / data.length;
    const avgMood = data.reduce((sum, d) => sum + d.moodScore, 0) / data.length;
    const avgFocus = data.reduce((sum, d) => sum + d.focusLevel, 0) / data.length;

    const overallScore = Math.round(
      (avgEnergy + avgSleep + avgActivity + avgNutrition + avgHydration + avgMood + avgFocus - avgStress) / 8
    );

    return {
      overallScore: Math.max(0, Math.min(100, overallScore)),
      stressLevel: Math.round(avgStress),
      energyLevel: Math.round(avgEnergy),
      sleepQuality: Math.round(avgSleep),
      physicalActivity: Math.round(avgActivity),
      nutritionScore: Math.round(avgNutrition),
      hydrationLevel: Math.round(avgHydration),
      moodScore: Math.round(avgMood),
      focusLevel: Math.round(avgFocus),
      lastUpdated: new Date(),
    };
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)' }}>
      <Routes>
        <Route 
          path="/" 
          element={
            <Dashboard 
              surveyData={surveyData} 
              healthMetrics={healthMetrics}
              onSurveySubmit={handleSurveySubmit}
            />
          } 
        />
        <Route 
          path="/survey" 
          element={
            <DailySurvey 
              onSubmit={handleSurveySubmit}
              lastSubmission={surveyData.length > 0 ? surveyData[surveyData.length - 1].date : null}
            />
          } 
        />
        <Route 
          path="/statistics" 
          element={
            <Statistics 
              surveyData={surveyData} 
              healthMetrics={healthMetrics}
            />
          } 
        />
        <Route path="/settings" element={<Settings />} />
      </Routes>
    </Box>
  );
};

export default App;
